import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { Logger, useLabel } from '@atlas/core';
import { Button, Icon, Label, Loader } from '@atlas/ui';

import useTranslate from 'src/common/I18n/Interpreter';
import {
  getSmarticoTournament,
  registerSmarticoTournament,
  useSmartico,
} from 'src/components/ClientProfile/Achievements/Smartico';

import Leaderboard from './Leaderboard';
import Prizes from './Prizes';
import RelatedGames from './RelatedGames';
import {
  calculateTimeLeft,
  cupIcon,
  formatDate,
  getBuyInText,
  getStatusConfig,
  getTournamentBtnLabel,
  TabsList,
} from './shares';

dayjs.extend(duration);

const TABS_DETAILS = [
  {
    text: 'rules',
  },
  {
    text: 'leaderboard',
  },
  {
    text: 'prize_pool',
  },
];

const MoreInfoTab = ({ description }) => <div dangerouslySetInnerHTML={{ __html: description }} />;

const SmarticoTournament = ({ tournamentPath }) => {
  const t = useTranslate();
  const labelDays = useLabel('days').charAt(0).toUpperCase();
  const labelHours = useLabel('hours').charAt(0).toUpperCase();
  const labelMinutes = useLabel('minutes').charAt(0).toUpperCase();
  const labelSeconds = useLabel('seconds').charAt(0).toUpperCase();

  const { tournamentId } = useParams();
  const { goBack } = useHistory();
  const { isReadySmartico } = useSmartico({ isLoadingScript: true });
  const [isShowImg, toggleVisibilityImg] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [activeTab, setActiveTab] = useState(TABS_DETAILS[0].text);

  useEffect(() => {
    if (isReadySmartico) {
      getSmarticoTournament({
        cb: tournament => {
          Logger.debug('Smartico.tournament', tournament);
          setTournament(tournament);
          toggleVisibilityImg(!!tournament?.image1);
          setLoading(false);
        },
        tournamentId,
      });
    }
  }, [isReadySmartico, tournamentId]);

  const durationDays = useMemo(() => {
    if (!tournament) return '';

    const numberMatchClock = Number(tournament.duration_ms);
    if (!Number.isFinite(numberMatchClock) || numberMatchClock < 0) {
      return '';
    }

    const durationTime = dayjs.duration(numberMatchClock, 'milliseconds');
    return durationTime.days();
  }, [tournament]);

  useEffect(() => {
    const calculateTime = () => {
      if (tournament?.is_in_progress) {
        const endTime = new Date(tournament.end_time).getTime();
        setTimeLeft(calculateTimeLeft(endTime));
      } else if (tournament?.is_upcoming) {
        const startTime = new Date(tournament.start_time).getTime();
        setTimeLeft(calculateTimeLeft(startTime));
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    const interval =
      tournament?.is_upcoming || tournament?.is_in_progress
        ? setInterval(calculateTime, 1000)
        : null;

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [tournament]);

  const btnClickHandler = useCallback(() => {
    if (tournament?.is_can_register) {
      registerSmarticoTournament({
        cb: () => {},
        tournamentId: tournament.instance_id,
      });
    }
  }, [tournament]);

  // Render tab content based on the active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case TABS_DETAILS[0].text:
        return <MoreInfoTab description={tournament.description} />;
      case TABS_DETAILS[1].text:
        return <Leaderboard players={tournament.players} />;
      case TABS_DETAILS[2].text:
        return <Prizes prizes={tournament.prizes} />;
      default:
        return null;
    }
  };

  const statusConfig = getStatusConfig(tournament);
  const handleError = () => toggleVisibilityImg(false);
  const buyInConfig = getBuyInText(tournament);

  const tabClickHandler = ({ text }) => setActiveTab(text);

  return (
    <div className="smartico-tournaments">
      {loading && (
        <div className="smartico-tournaments-loader">
          <Loader className="absolute" />
        </div>
      )}
      {!loading && !tournament && <Redirect to={tournamentPath} />}
      {!loading && !!tournament && (
        <div className="smartico-tournament">
          <div className="smartico-tournament-card-back-wrp" onClick={goBack}>
            <Icon className="smartico-tournament-card-back" name="back" />
          </div>
          <div className="smartico-tournament-card-top">
            <div
              className="smartico-tournament-card-title"
              dangerouslySetInnerHTML={{ __html: tournament.name }}
            />
            {!!statusConfig.status && (
              <Label
                className={`smartico-tournaments-status ${statusConfig.status}`}
                message={statusConfig.status}
              />
            )}
          </div>
          <div className="smartico-tournament-card">
            {isShowImg && (
              <img
                alt=""
                className="smartico-tournaments-card-back"
                onError={handleError}
                src={tournament.image1}
              />
            )}
            <div className="smartico-tournament-card-footer">
              <div className="smartico-tournament-card-prize">
                {!!tournament.prize_pool_short && (
                  <div>
                    <div className="smartico-tournaments-card-prize-value">
                      {tournament.prize_pool_short}
                    </div>
                    <div className="smartico-tournaments-card-prize-info">
                      <img alt="cup" height={24} src={cupIcon} width={24} />
                      <Label
                        className="smartico-tournaments-card-prize-info-text"
                        message="smartico_tournaments_prize_pool"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Button
            className="smartico-tournaments-card-info-btn"
            disabled={!tournament.is_can_register}
            label={getTournamentBtnLabel(tournament)}
            onClick={btnClickHandler}
          />
          <div className="smartico-tournament-card-desc">
            <div className="smartico-tournament-card-dates">
              {formatDate(tournament.start_time)} - {formatDate(tournament.end_time)}
            </div>
            <div className="smartico-tournament-card-info">
              <div className="smartico-tournament-card-info-section">
                <Label
                  className="smartico-tournaments-card-info-title"
                  message="smartico_tournaments_buy_in"
                />
                <div className="smartico-tournaments-card-info-value">
                  {!!buyInConfig[0] && (
                    <span className="smartico-tournaments-card-info-text">{buyInConfig[0]}</span>
                  )}
                  {!!buyInConfig[1] && (
                    <Label
                      className="smartico-tournaments-card-info-text"
                      message={buyInConfig[1]}
                    />
                  )}
                </div>
              </div>
              <div className="smartico-tournament-card-info-section">
                <Label
                  className="smartico-tournaments-card-info-title"
                  message="smartico_tournaments_registered"
                />
                <div className="smartico-tournaments-card-info-value">
                  <span className="smartico-tournaments-card-info-text">
                    {`${tournament.registration_count} / ${tournament.players_max_count}`}
                  </span>
                </div>
              </div>
              <div className="smartico-tournament-card-info-section">
                <Label
                  className="smartico-tournaments-card-info-title"
                  message="smartico_tournaments_duration"
                />
                <div className="smartico-tournaments-card-info-value">
                  <span className="smartico-tournaments-card-info-text">
                    {`${t(`${durationDays}_days`)}`}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {!!statusConfig.timer && (
            <div className="smartico-tournaments-timer">
              <Label className="smartico-tournaments-timer-title" message={statusConfig.timer} />
              <div className="smartico-tournaments-timer-value">
                <span className="smartico-tournaments-timer-section">
                  {`${String(timeLeft.days).padStart(2, '0')}${labelDays}`}
                </span>
                <div className="smartico-tournaments-timer-divider">:</div>
                <span className="smartico-tournaments-timer-section">
                  {`${String(timeLeft.hours).padStart(2, '0')}${labelHours}`}
                </span>
                <div className="smartico-tournaments-timer-divider">:</div>
                <span className="smartico-tournaments-timer-section">
                  {`${String(timeLeft.minutes).padStart(2, '0')}${labelMinutes}`}
                </span>
                <div className="smartico-tournaments-timer-divider">:</div>
                <span className="smartico-tournaments-timer-section">
                  {`${String(timeLeft.seconds).padStart(2, '0')}${labelSeconds}`}
                </span>
              </div>
            </div>
          )}
          <div className="smartico-tournament-card-info-more">
            <div className="smartico-tournament-tabs-wrapper">
              <TabsList categoryId={activeTab} onClick={tabClickHandler} tabs={TABS_DETAILS} />
              <div className="smartico-tournament-tabs-content">{renderTabContent()}</div>
            </div>
            {!!tournament.related_games?.length && (
              <RelatedGames relatedGames={tournament.related_games} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmarticoTournament;
